import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

const faqData = [
  {
    id: 1,
    question: "Comment fonctionne le crédit d'impôt de 50% ?",
    answer: "Le crédit d'impôt de 50% s'applique uniquement aux prestations éligibles au Service à la Personne (SAP) : ménage, jardinage, bricolage et aide administrative. Les autres services comme la gestion Airbnb (qui inclut le check-in/check-out) ou le nettoyage de véhicule ne sont pas éligibles. Ce crédit s'applique jusqu'à 12 000€ de dépenses par an. Nous vous fournissons l'attestation fiscale annuelle nécessaire pour votre déclaration d'impôts."
  },
  {
    id: 2,
    question: "Qu'est-ce que l'avance immédiate de crédit d'impôt ?",
    answer: "L'avance immédiate vous permet de ne payer que 50% du montant de la prestation au moment du règlement, uniquement pour les services éligibles au SAP. L'État verse directement les 50% restants à notre société. Vous n'avez donc plus besoin d'attendre votre déclaration d'impôts pour bénéficier de l'avantage fiscal. Ce service est gratuit et géré par l'URSSAF."
  },
  {
    id: 3,
    question: "Dans quelles villes intervenez-vous ?",
    answer: "Nous intervenons dans un rayon de 50 km autour de notre siège à Méru (60110). Cela inclut notamment Beauvais, Creil, Compiègne, Senlis, Chantilly, Clermont, Chambly, L'Isle-Adam, Pontoise et de nombreuses autres communes de l'Oise et des départements limitrophes. Contactez-nous pour vérifier si votre commune est couverte."
  },
  {
    id: 4,
    question: "Comment puis-je réserver une prestation ?",
    answer: "Vous pouvez nous contacter par téléphone au 09 81 54 38 42, via notre formulaire de contact en ligne, ou en demandant un devis gratuit sur notre site. Nous vous recontacterons sous 24h. Un acompte est demandé à la signature du devis pour confirmer votre réservation, puis nous planifions l'intervention selon vos disponibilités."
  },
  {
    id: 5,
    question: "Quels services proposez-vous ?",
    answer: "Nous proposons deux types de services. Les services éligibles au crédit d'impôt SAP (50%) : ménage, jardinage, bricolage et aide administrative. Et des services complémentaires à part : aide au déménagement, nettoyage de véhicules (intérieur et extérieur) et gestion de locations Airbnb. Tous nos services sont réalisés avec le même niveau de qualité et de professionnalisme."
  },
  {
    id: 6,
    question: "Vos intervenants sont-ils qualifiés ?",
    answer: "Oui, tous nos intervenants sont rigoureusement sélectionnés, formés et accompagnés. Ils sont déclarés et nous attachons une grande importance à la qualité de service et au respect de votre domicile. Nous travaillons uniquement avec des professionnels de confiance."
  },
  {
    id: 7,
    question: "Puis-je annuler ou reporter une prestation ?",
    answer: "Vous pouvez reporter une prestation selon nos disponibilités, contactez-nous le plus tôt possible pour réorganiser le rendez-vous. En cas d'annulation, l'acompte versé à la signature du devis n'est pas remboursable. Nous vous encourageons donc à bien vérifier vos disponibilités avant de confirmer votre réservation."
  },
  {
    id: 8,
    question: "Quels sont vos modes de paiement ?",
    answer: "Nous acceptons les paiements en espèces, par virement bancaire, chèque et CESU (Chèque Emploi Service Universel). Pour les prestations éligibles au SAP, si vous optez pour l'avance immédiate de crédit d'impôt, vous ne payez que 50% du montant. Une facture détaillée vous est remise après chaque prestation."
  }
];

export const FAQ = () => {
  const [openItems, setOpenItems] = useState([]);

  const toggleItem = (id) => {
    setOpenItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  return (
    <section id="faq" className="py-20 bg-[#F8FAF9] font-['Poppins']">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#153254]/10 text-[#153254] px-4 py-2 rounded-full text-sm font-medium mb-4">
              <HelpCircle className="h-4 w-4" />
              Questions fréquentes
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#153254] mb-4">
              Foire aux questions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Retrouvez les réponses aux questions les plus fréquemment posées 
              sur nos services et le crédit d'impôt.
            </p>
          </div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {faqData.map((item) => (
              <div 
                key={item.id}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-200 hover:shadow-md"
              >
                <button
                  onClick={() => toggleItem(item.id)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left gap-4"
                  aria-expanded={openItems.includes(item.id)}
                  data-testid={`faq-question-${item.id}`}
                >
                  <span className="font-semibold text-[#153254] text-base md:text-lg pr-4">
                    {item.question}
                  </span>
                  <ChevronDown 
                    className={`h-5 w-5 text-[#D4AF37] flex-shrink-0 transition-transform duration-300 ${
                      openItems.includes(item.id) ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                
                <div 
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    openItems.includes(item.id) ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-6 pb-5 pt-0">
                    <div className="border-t border-gray-100 pt-4">
                      <p className="text-gray-600 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">
              Vous avez une autre question ?
            </p>
            <a 
              href="#contact"
              className="inline-flex items-center gap-2 bg-[#153254] hover:bg-[#153254]/90 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
              data-testid="faq-contact-btn"
            >
              Contactez-nous
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
